import React, { useEffect, useRef, useState, useCallback } from "react";
import Plotly from "plotly.js-dist-min";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Tooltip from "@mui/material/Tooltip";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import ChartIcon from "@mui/icons-material/Addchart";
import { useMqtt } from "context/MQTTContext";
import RealtimeSoundLevels from "components/RealTimeSoundLevels";
import { useNavigate } from "react-router-dom";

interface RealtimeGaugeTestProps {
    topicPrefix: string;
}

const RealtimeGaugeTest: React.FC<RealtimeGaugeTestProps> = ({ topicPrefix }) => {
    const gaugeRef = useRef<HTMLDivElement>(null);
    const simplePopupChartRef = useRef<HTMLDivElement>(null);
    const { dBA, thirdOctaveData, connectionStatus, error } = useMqtt();
    const [maxThirdOctaveData, setMaxThirdOctaveData] = useState<number[]>([]);
    const [showSimplePopup, setShowSimplePopup] = useState<boolean>(false);
    const [showComplexPopup, setShowComplexPopup] = useState<boolean>(false);
    const navigate = useNavigate();

    // Gauge update logic (unchanged)
    const updateGauge = useCallback((dBAValue: number) => {
        if (!gaugeRef.current) return;
        const levels = [30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100];
        const barColors = levels.map((level) => {
            if (dBAValue >= level) {
                if (level < 70) return "green";
                else if (level < 80) return "yellow";
                else if (level < 90) return "orange";
                else return "red";
            }
            return "lightgray";
        });
        const barWidth = 3;
        const trace: Partial<Plotly.PlotData> = {
            x: Array(levels.length).fill(0.25),
            y: levels,
            type: "bar",
            marker: { color: barColors, line: { width: 1 } },
            orientation: "h",
            width: barWidth,
            showlegend: false,
            hoverlabel: { bgcolor: "dark grey", font: { color: "white" } },
        };
        const layout: Partial<Plotly.Layout> = {
            title: "",
            margin: { t: 0, b: 0, l: 50, r: 20 },
            xaxis: { range: [0, 0.5], visible: false },
            yaxis: {
                range: [25, 105],
                tickvals: levels.filter((_, index) => index % 2 === 0),
                ticktext: levels.filter((_, index) => index % 2 === 0).map(String),
                showgrid: false,
                zeroline: false,
            },
            height: 200,
            autosize: true,
            plot_bgcolor: "#ffffff",
        };
        Plotly.newPlot(gaugeRef.current, [trace], layout, {
            displayModeBar: false,
            responsive: true,
        });
    }, []);

    useEffect(() => {
        if (dBA !== null) {
            updateGauge(dBA);
        }
    }, [dBA, updateGauge]);

    // Update maxThirdOctaveData based on incoming thirdOctaveData.
    useEffect(() => {
        if (thirdOctaveData.length > 0) {
            setMaxThirdOctaveData((prevMax) => {
                if (!prevMax || prevMax.length === 0) return thirdOctaveData;
                return thirdOctaveData.map((val: number, idx: number) =>
                    Math.max(val, prevMax[idx] ?? 0)
                );
            });
        }
    }, [thirdOctaveData]);

    // Define the update function for the simple (1/3 octave band) chart.
    const updateSimpleChart = useCallback(() => {
        if (!simplePopupChartRef.current) return;
        const xLabels = [
            '20', '25', '31.5', '40', '50', '63', '80', '100', '125', '160',
            '200', '250', '315', '400', '500', '630', '800', '1000', '1250',
            '1600', '2000', '2500', '3150', '4000', '5000', '6300', '8000'
        ];
        const indices = xLabels.map((_, i) => i);
        const currentTrace = {
            x: indices,
            y: thirdOctaveData,
            type: 'bar' as const,
            name: 'Current Levels',
            width: 0.8,
            marker: { color: '#6594B3' }
        };
        const maxX: number[] = [];
        const maxY: number[] = [];
        for (let i = 0; i < maxThirdOctaveData.length; i++) {
            maxX.push(i - 0.4, i + 0.4, NaN);
            maxY.push(maxThirdOctaveData[i], maxThirdOctaveData[i], NaN);
        }
        const maxTrace = {
            x: maxX,
            y: maxY,
            type: 'scatter' as const,
            mode: 'lines' as const,
            line: { dash: 'solid' as const, color: '#156082' },
            name: 'Max Levels'
        };
        const layout: Partial<Plotly.Layout> = {
            title: '1/3 Octave Band Levels',
            xaxis: {
                title: 'Frequency Bands',
                tickmode: 'array',
                tickvals: indices,
                ticktext: xLabels,
            },
            yaxis: { title: 'Level (dB)' }
        };
        Plotly.newPlot(simplePopupChartRef.current, [currentTrace, maxTrace], layout, { responsive: true });
    }, [thirdOctaveData, maxThirdOctaveData]);

    useEffect(() => {
        if (showSimplePopup && thirdOctaveData.length > 0 && maxThirdOctaveData.length > 0) {
            updateSimpleChart();
        }
    }, [thirdOctaveData, maxThirdOctaveData, showSimplePopup, updateSimpleChart]);

    return (
        <MDBox
            sx={{
                position: "relative",
                width: "100%",
                padding: "16px",
                boxSizing: "border-box",
            }}
        >
            <MDBox sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                {connectionStatus !== "connected" && (
                    <MDTypography variant="caption" color={connectionStatus === "error" ? "error" : "warning"}>
                        {connectionStatus === "error" ? "Connection Error" : "Connecting..."}
                    </MDTypography>
                )}
                <MDTypography variant="h6">Live Noise Level</MDTypography>
                <div ref={gaugeRef} style={{ width: "125px", height: "200px" }} />
                <MDBox sx={{ fontSize: "18px", fontFamily: '"Open Sans", Verdana, Arial, sans-serif' }}>
                    {error ? (
                        <MDTypography color="error">Failed to load live data</MDTypography>
                    ) : dBA !== null ? (
                        `${dBA.toFixed(1)} dBA`
                    ) : (
                        "Loading..."
                    )}
                </MDBox>
            </MDBox>
            {/* Icon to launch complex display (RealtimeSoundLevels) */}
            <Tooltip title="Sound Level Meter" arrow>
                <ViewSidebarIcon
                    fontSize="medium"
                    sx={{
                        position: "absolute",
                        bottom: 45,
                        right: 8,
                        cursor: "pointer",
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        // Navigate to the new page, passing the topicPrefix in the URL.
                        navigate(`/slm?topicPrefix=${topicPrefix}`);
                    }}
                />
            </Tooltip>
            {/* Icon to launch simple 1/3 octave band display */}
            <Tooltip title="View 1/3 Octave Band Data" arrow>
                <ChartIcon
                    fontSize="medium"
                    sx={{
                        position: "absolute",
                        bottom: 8,
                        right: 8,
                        cursor: "pointer",
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        setShowSimplePopup(true);
                    }}
                />
            </Tooltip>
            {/* Modal for the complex display */}
            <Dialog open={showComplexPopup} onClose={() => setShowComplexPopup(false)} maxWidth="md" fullWidth>
                <DialogTitle>Sound Level Meter</DialogTitle>
                <DialogContent>
                    <RealtimeSoundLevels />
                </DialogContent>
            </Dialog>
            {/* Modal for the simple 1/3 octave band display */}
            <Dialog open={showSimplePopup} onClose={() => setShowSimplePopup(false)} maxWidth="md" fullWidth>
                <DialogTitle>1/3 Octave Band Levels</DialogTitle>
                <DialogContent>
                    <div ref={simplePopupChartRef} style={{ width: "100%", height: "400px" }} />
                </DialogContent>
            </Dialog>
        </MDBox>
    );
};

export default RealtimeGaugeTest;
